/**
 * Promotion Type Config — Single Source of Truth
 *
 * Maps merchant-friendly promotion types to form fields, ERPNext
 * Pricing Rule values, and client-side price computation.
 */

// ── Types ────────────────────────────────────────────────────

export type PromotionTypeId =
  | "percentage_off"
  | "fixed_sale_price"
  | "quantity_deal"
  | "customer_group_price"
  | "group_order_deal";

export interface PromotionTypeConfig {
  id: PromotionTypeId;
  label: string;
  sublabel: string;
  icon: string;
  hasEndDate: boolean;
  hasAppliesTo: boolean;
  hasMinQty: boolean;
  /** Which numeric field this type uses */
  valueField: "discount_percentage" | "rate";
  valueLabel: string;
  valuePrefix?: string;
  valueSuffix?: string;
  /**
   * Coupon-based orchestration types are NOT single-item automatic rules — they
   * bundle perks (free shipping / embroidery / conditional customization) and are
   * built through the full Pricing Rule wizard (`admin_coupon.create_promotion`),
   * not the item-level RunASaleModal. The modal filters these out of its grid.
   */
  couponBased?: boolean;
  hasFreeShipping?: boolean;
  hasFreeEmbroidery?: boolean;
  /** Exposes the "conditional customization" (template/field/action) input in the wizard. */
  hasConditionalCustomization?: boolean;
}

export interface PromotionFormState {
  promotionType: PromotionTypeId | null;
  saleName: string;
  discountPercentage: string;
  rate: string;
  minQty: string;
  customerGroup: string;
  validFrom: string;
  validUpto: string;
  applyMultiplePricingRules: boolean;
}

// ── Config ───────────────────────────────────────────────────

export const PROMOTION_TYPES: PromotionTypeConfig[] = [
  {
    id: "percentage_off",
    label: "Percentage Off",
    sublabel: "e.g. 15% off for everyone",
    icon: "%",
    hasEndDate: true,
    hasAppliesTo: true,
    hasMinQty: false,
    valueField: "discount_percentage",
    valueLabel: "Discount",
    valueSuffix: "%",
  },
  {
    id: "fixed_sale_price",
    label: "Fixed Sale Price",
    sublabel: "e.g. ฿199 during the weekend",
    icon: "฿",
    hasEndDate: true,
    hasAppliesTo: true,
    hasMinQty: false,
    valueField: "rate",
    valueLabel: "Sale price",
    valuePrefix: "฿",
  },
  {
    id: "quantity_deal",
    label: "Quantity Deal",
    sublabel: "e.g. Buy 5+, pay 10% less",
    icon: "📦",
    hasEndDate: true,
    hasAppliesTo: true,
    hasMinQty: true,
    valueField: "discount_percentage",
    valueLabel: "Discount",
    valueSuffix: "%",
  },
  {
    id: "customer_group_price",
    label: "Customer Group Price",
    sublabel: "e.g. VIP members always pay ฿220",
    icon: "👤",
    hasEndDate: false,
    hasAppliesTo: false,
    hasMinQty: false,
    valueField: "rate",
    valueLabel: "Their price",
    valuePrefix: "฿",
  },
  {
    id: "group_order_deal",
    label: "Group / Bulk Deal",
    sublabel: "e.g. order 10+ → free shipping + free logo",
    icon: "👥",
    hasEndDate: true,
    hasAppliesTo: true,
    hasMinQty: true,
    // Cart-level coupon deal — no single-item price change, so the numeric field
    // is nominal; computePreviewPrice returns the base price unchanged.
    valueField: "discount_percentage",
    valueLabel: "Discount",
    valueSuffix: "%",
    couponBased: true,
    hasFreeShipping: true,
    hasFreeEmbroidery: true,
    hasConditionalCustomization: true,
  },
];

// ── Helpers ──────────────────────────────────────────────────

export function getTypeConfig(id: PromotionTypeId): PromotionTypeConfig {
  return PROMOTION_TYPES.find((t) => t.id === id)!;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function generateSaleName(): string {
  const now = new Date();
  return `${MONTHS[now.getMonth()]} Sale ${now.getFullYear()}`;
}

export function getTodayISO(): string {
  return new Date().toISOString().split("T")[0];
}

/**
 * Compute the preview price based on promotion type and value.
 * Mirrors the backend _compute_price_with_promotion logic.
 */
export function computePreviewPrice(
  basePrice: number | null,
  typeId: PromotionTypeId | null,
  form: PromotionFormState
): number | null {
  if (basePrice === null || basePrice === undefined || !typeId) return null;

  // Group / Bulk deals are coupon-based cart-level promotions: they don't change
  // a single item's price, so the per-item preview is just the base price.
  if (typeId === "group_order_deal") return basePrice;

  if (typeId === "percentage_off" || typeId === "quantity_deal") {
    const pct = parseFloat(form.discountPercentage);
    if (isNaN(pct) || pct <= 0) return basePrice;
    return Math.round(basePrice * (1 - pct / 100) * 100) / 100;
  }

  if (typeId === "fixed_sale_price" || typeId === "customer_group_price") {
    const r = parseFloat(form.rate);
    if (isNaN(r) || r <= 0) return basePrice;
    return Math.round(r * 100) / 100;
  }

  return basePrice;
}

export function createEmptyFormState(): PromotionFormState {
  return {
    promotionType: null,
    saleName: generateSaleName(),
    discountPercentage: "",
    rate: "",
    minQty: "",
    customerGroup: "All Customers",
    validFrom: getTodayISO(),
    validUpto: "",
    applyMultiplePricingRules: false,
  };
}

// ── Reverse mapping (for edit mode) ─────────────────────────

interface PromotionLike {
  rate_or_discount: string;
  discount_percentage: number | null;
  rate: number | null;
  min_qty: number | null;
  customer_group: string | null;
  applicable_for: string | null;
  title: string;
  valid_from: string | null;
  valid_upto: string | null;
  apply_multiple_pricing_rules?: 0 | 1;
}

/**
 * Infer the PromotionTypeId from an existing Pricing Rule's fields.
 *
 * Returns null for unsupported types (e.g. "Discount Amount") —
 * the "Edit" action should be disabled in this case.
 */
export function inferPromotionTypeFromRule(
  promo: PromotionLike
): PromotionTypeId | null {
  const rod = promo.rate_or_discount || "";

  if (rod === "Discount Percentage") {
    return (promo.min_qty && Number(promo.min_qty) > 0)
      ? "quantity_deal"
      : "percentage_off";
  }

  if (rod === "Rate") {
    return (promo.applicable_for === "Customer Group" || promo.customer_group)
      ? "customer_group_price"
      : "fixed_sale_price";
  }

  // "Discount Amount" or unknown — not editable via the modal.
  // Coupon-based "group_order_deal" rules are never inferred here: they are
  // authored/edited through the Pricing Rule wizard + detail page, not the
  // item-level RunASaleModal, so this stays null for them (Edit stays disabled).
  return null;
}

/**
 * Pre-fill a PromotionFormState from an existing promotion.
 * Used by RunASaleModal in edit mode.
 */
export function createFormStateFromPromotion(
  promo: PromotionLike
): PromotionFormState {
  const typeId = inferPromotionTypeFromRule(promo);

  return {
    promotionType: typeId,
    saleName: promo.title || "",
    discountPercentage: promo.discount_percentage != null
      ? String(promo.discount_percentage)
      : "",
    rate: promo.rate != null ? String(promo.rate) : "",
    minQty: promo.min_qty != null ? String(promo.min_qty) : "",
    customerGroup: promo.customer_group || "All Customers",
    validFrom: promo.valid_from || getTodayISO(),
    validUpto: promo.valid_upto || "",
    applyMultiplePricingRules: !!promo.apply_multiple_pricing_rules,
  };
}
